import type { SampleArea } from '@team-milva/lab-models/resources'
import type { Analysis } from '@/globals/typescript/models/Analysis'

export interface IAnalysisPickerOption {
  value: string
  text: string
  /** Small line under the name — e.g. "I segment". */
  description?: string
  /** Rendered checked-but-locked by UiInputCheckboxGroupVee. */
  isDisabled?: boolean
  /**
   * Hides the checkbox itself while disabled — for options that are shown for
   * context but are not a choice here at all (e.g. an analysis already claimed
   * by another price line of the same TAT), where an empty box would read as
   * "tickable".
   */
  hideCheckWhenDisabled?: boolean
}

export interface IAnalysisPickerGroup {
  id: string
  name: string
  options: IAnalysisPickerOption[]
}

/**
 * Groups analyses by sample area for the analysis picker modals.
 *
 * Extracted because three modals need the identical grouping — the pricing
 * segment's analysis picker, the customer's analysis picker, and the
 * customer's per-TAT analysis picker. The first two had already diverged into
 * two copies of this logic; a third would have made the same drift that hit
 * the price input and the TAT-compatibility rule earlier in this work.
 *
 * The callers differ only in `toOption`, which decides per analysis whether it
 * appears at all (`null` hides it) and how it is labelled or locked.
 *
 * Sorting matches the previous inline implementations: sample areas and
 * analyses both alphabetical by localised name, with an "ungrouped" bucket
 * appended for analyses belonging to no active sample area. Groups that end up
 * empty are dropped.
 */
export function groupAnalysesBySampleArea({
  analyses,
  sampleAreas,
  locale,
  ungroupedLabel,
  toOption,
}: {
  analyses: Analysis[]
  sampleAreas: SampleArea[]
  locale: string
  ungroupedLabel: string
  toOption: (analysis: Analysis) => IAnalysisPickerOption | null
}): IAnalysisPickerGroup[] {
  const buildOptions = (list: Analysis[]): IAnalysisPickerOption[] =>
    list
      .map(toOption)
      .filter((option): option is IAnalysisPickerOption => option !== null)
      .sort((a, b) => a.text.localeCompare(b.text))

  const activeSampleAreas = [...sampleAreas]
    .filter((x) => x.activeState === 'active')
    .sort((a, b) => a.getName(locale).localeCompare(b.getName(locale)))

  const groups: IAnalysisPickerGroup[] = activeSampleAreas.map((sampleArea) => ({
    id: sampleArea.id,
    name: sampleArea.getName(locale),
    options: buildOptions(
      analyses.filter((analysis) =>
        analysis.sampleAreaIDs.includes(sampleArea.id)
      )
    ),
  }))

  const groupedSampleAreaIDs = new Set(activeSampleAreas.map((x) => x.id))
  const ungroupedOptions = buildOptions(
    analyses.filter((analysis) =>
      analysis.sampleAreaIDs.every((id) => !groupedSampleAreaIDs.has(id))
    )
  )

  if (ungroupedOptions.length > 0) {
    groups.push({
      id: 'ungrouped',
      name: ungroupedLabel,
      options: ungroupedOptions,
    })
  }

  return groups.filter((group) => group.options.length > 0)
}
